import React, { useState } from 'react';

import { IconButton } from '@/design-system/icon-button';

interface MapLegendProps {
  /** True when the "show other hubs' coverage" toggle is on. */
  showOtherCoverage: boolean;
  /** True when dispatch barriers are toggled on AND loaded. */
  showBarriers?: boolean;
  /** True when the driver ("mitra") badge layer is toggled on. */
  showDriverBadges?: boolean;
}

/**
 * Tiny legend pinned to the bottom-left of the map. Explains the cell
 * states so the dashed red border reads as "click again to reassign"
 * not as a generic error. Collapsible to a single chip.
 */
const MapLegend: React.FC<MapLegendProps> = ({
  showOtherCoverage,
  showBarriers = false,
  showDriverBadges = false,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        className="inline-flex items-center gap-1 rounded-2xl border border-stroke-soft-200 bg-bg-white-0 px-2.5 py-1.5 text-[11px] font-semibold tracking-tight text-text-sub-600 shadow-sm hover:bg-bg-weak-50 hover:text-text-strong-950"
        title="Show legend"
      >
        Legend
        <i className="ri-arrow-up-s-line text-[14px]" />
      </button>
    );
  }

  return (
    <div className="rounded-2xl border border-stroke-soft-200 bg-bg-white-0 px-3 py-2 text-[11px] shadow-sm">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <p className="label-eyebrow">Legend</p>
        <IconButton
          type="button"
          tone="neutral"
          style="ghost"
          size="xs"
          onClick={() => setCollapsed(true)}
          aria-label="Collapse legend"
          title="Collapse"
        >
          <i className="ri-arrow-down-s-line" />
        </IconButton>
      </div>
      <div className="space-y-1">
        <LegendRow
          swatchStyle={{
            background: 'rgba(94,42,172,0.36)',
            border: '1.5px solid rgba(94,42,172,0.95)',
          }}
          label="This hub's cells"
        />
        {showOtherCoverage && (
          <LegendRow
            swatchStyle={{
              background: 'rgba(15,118,110,0.18)',
              border: '1.5px solid rgba(15,118,110,0.6)',
            }}
            label="Other hubs' cells"
          />
        )}
        <LegendRow
          swatchStyle={{
            background: 'rgba(220,38,38,0.20)',
            border: '1.5px dashed rgba(220,38,38,0.7)',
          }}
          label="Conflict (reassign?)"
        />
        {showBarriers && (
          <LegendRow
            swatchStyle={{
              background: 'rgba(31,41,55,0.65)',
              border: 'none',
              height: 3,
              borderRadius: 2,
              alignSelf: 'center',
            }}
            label="Dispatch barrier (toll/river)"
          />
        )}
        {showDriverBadges && (
          <>
            <div className="flex items-center gap-2">
              <span className="grid h-3 w-3 shrink-0 place-items-center rounded-full bg-primary">
                <i
                  className="ri-user-fill text-[8px] text-static-white"
                  aria-hidden="true"
                />
              </span>
              <span className="text-text-sub-600">Mitra on zone (click)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="grid h-3 w-3 shrink-0 place-items-center rounded-full border border-dashed border-warning-base">
                <i
                  className="ri-user-fill text-[7px] text-warning-base"
                  aria-hidden="true"
                />
              </span>
              <span className="text-text-sub-600">No mitra assigned</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

interface LegendRowProps {
  swatchStyle: React.CSSProperties;
  label: string;
}

const LegendRow: React.FC<LegendRowProps> = ({ swatchStyle, label }) => (
  <div className="flex items-center gap-2">
    <span className="h-3 w-3 shrink-0" style={swatchStyle} />
    <span className="text-text-sub-600">{label}</span>
  </div>
);

export default MapLegend;
